// Vuex
import BookingService from '@/services/BookingService'
import {
  BookingSummary,
  TravellerSummary,
} from '@/services/BookingService.interface'
import { ActionContext, GetterTree } from 'vuex'

// Extended Vuex types and store
import { State, TypedActionTree, LoadingErrorState } from '@/store/types'

// Config
import { BookingStatus, isTravelskiExpress } from '@/config' // Import the enum and helper function

// Utils
import { resolveTravellerId } from '@/utils/resolveTravellerId'

// Define actions for booking
export type BookingActions =
  | 'fetchBookings'
  | 'selectBooking'
  | 'unselectBooking'
  | 'updateTraveller'

// Define the type for the action context
type BookingActionContext = ActionContext<BookingState, State>

// Vuex State for Bookings
export interface BookingState extends LoadingErrorState<BookingActions> {
  bookings: BookingSummary[]
  selectedBooking: BookingSummary | null
}

// Initial state
const state: BookingState = {
  bookings: [],
  selectedBooking: null, // Default to no selected booking
  error: {},
  loading: {},
}

// Mutations
const mutations = {
  setBookings(state: BookingState, bookings: BookingSummary[]) {
    state.bookings = bookings
  },
  setLoading(
    state: BookingState,
    { action, status }: { action: BookingActions; status: boolean }
  ) {
    state.loading[action] = status
  },
  setError(
    state: BookingState,
    { action, message }: { action: BookingActions; message: string | null }
  ) {
    state.error[action] = message
  },
  setSelectedBooking(state: BookingState, booking: BookingSummary | null) {
    state.selectedBooking = booking
  },
  updateSelectedBookingTraveller(
    state: BookingState,
    updates: TravellerSummary
  ) {
    const { selectedBooking } = state
    if (!selectedBooking) return

    const travellerToUpdate = selectedBooking.travellers.find(
      traveller => traveller.id === updates.id
    )
    if (!travellerToUpdate) return

    const index = selectedBooking.travellers.indexOf(travellerToUpdate)
    if (index > -1) {
      selectedBooking.travellers[index] = { ...updates }
    }
  },
}

// Actions
const actions: TypedActionTree<BookingActions, BookingState, State> = {
  async fetchBookings(
    { commit }: BookingActionContext,
    {
      userEmail,
      page = 1,
      pageSize = 400,
    }: { userEmail: string; page?: number; pageSize?: number }
  ) {
    const action = 'fetchBookings'
    commit('setLoading', { action, status: true })
    commit('setError', { action, message: null })
    try {
      const bookingService = new BookingService()
      const bookings = await bookingService.getBookings(
        userEmail,
        page,
        pageSize
      )
      commit('setBookings', bookings)
    } catch (error) {
      console.error('Error fetching bookings:', error)
      commit('setError', { action, message: 'Failed to fetch bookings.' })
      throw error
    } finally {
      commit('setLoading', { action, status: false })
    }
  },

  selectBooking({ commit }: BookingActionContext, booking: BookingSummary) {
    const action = 'selectBooking'
    commit('setLoading', { action, status: true })
    try {
      commit('setSelectedBooking', booking)
    } catch (error) {
      console.error('Error selecting booking:', error)
      commit('setError', { action, message: 'Failed to select booking.' })
    } finally {
      commit('setLoading', { action, status: false })
    }
  },

  unselectBooking({ commit }: BookingActionContext) {
    const action = 'unselectBooking'
    commit('setLoading', { action, status: true })
    try {
      commit('setSelectedBooking', null)
    } catch (error) {
      console.error('Error unselecting booking:', error)
      commit('setError', { action, message: 'Failed to unselect booking.' })
    } finally {
      commit('setLoading', { action, status: false })
    }
  },

  async updateTraveller(
    { commit, state, rootState }: BookingActionContext,
    updates: TravellerSummary
  ) {
    const action = 'updateTraveller'
    commit('setLoading', { action, status: true })
    commit('setError', { action, message: null })

    try {
      if (!state.selectedBooking) {
        throw new Error('No selected booking to update.')
      }

      const userEmail = rootState.auth?.user?.email
      if (!userEmail) {
        throw new Error('Failed to update traveller.')
      }

      const bookingService = new BookingService()
      const bookingReference = state.selectedBooking.reference

      const freshBookings = await bookingService.getBookings(userEmail)
      commit('setBookings', freshBookings)

      const freshBooking = freshBookings.find(
        booking => booking.reference === bookingReference
      )
      if (!freshBooking) {
        throw new Error('Failed to update traveller.')
      }

      commit('setSelectedBooking', freshBooking)

      const resolvedId = resolveTravellerId(updates, freshBooking.travellers)
      const payload: TravellerSummary = { ...updates, id: resolvedId }

      const updateData = await bookingService.updateTravellers(
        [payload],
        bookingReference
      )

      if (updateData.message.errors.length) {
        throw new Error('Failed to update traveller.')
      }

      commit('updateSelectedBookingTraveller', payload)
    } catch (error) {
      console.error('Error updating traveller:', error)
      commit('setError', { action, message: 'Failed to update traveller.' })
      throw error
    } finally {
      commit('setLoading', { action, status: false })
    }
  },
}

// Getters
const getters: GetterTree<BookingState, State> = {
  /**
   * Check if any action is loading
   */
  isLoadingAny: (state: BookingState) => {
    return Object.values(state.loading).some(status => status)
  },

  /**
   * Check if a specific action is loading
   */
  isLoading: (state: BookingState) => (action: BookingActions) => {
    return !!state.loading[action]
  },

  /**
   * Check if there are any errors
   */
  hasErrorAny: (state: BookingState) => {
    return Object.values(state.error).some(message => message !== null)
  },

  /**
   * Get the error message for a specific action
   */
  getError: (state: BookingState) => (action: BookingActions) => {
    return state.error[action] || null
  },

  /**
   * Get all user bookings
   */
  getUserBookings: (state: BookingState): BookingSummary[] => {
    return state.bookings
  },

  /**
   * Get current bookings
   * For Travelski Express, only show reservations
   */
  currentBookings: (state: BookingState): BookingSummary[] => {
    return state.bookings.filter(booking => {
      // For Travelski Express, only show reservations
      if (isTravelskiExpress(booking.liheId, booking.liheName, booking.packageCode)) {
        return booking.status === BookingStatus.Reservation
      }
      // For other bookings, keep the original logic
      return booking.status === BookingStatus.Reservation
    })
  },

  /**
   * Get past bookings
   * For Travelski Express, also show past bookings (billed, cancelled)
   */
  pastBookings: (state: BookingState): BookingSummary[] => {
    return state.bookings.filter(booking => {
      // For Travelski Express, show past bookings too
      if (isTravelskiExpress(booking.liheId, booking.liheName, booking.packageCode)) {
        return booking.status === BookingStatus.Billed ||
               booking.status === BookingStatus.CancelledAfterBilling
      }
      // For other bookings, keep the original logic
      return booking.status === BookingStatus.Billed ||
             booking.status === BookingStatus.CancelledAfterBilling
    })
  },

  /**
   * Get quotes and cart in progress
   * For Travelski Express, only show reservations; filter out quotes and carts
   */
  quotesAndCartInProgress: (state: BookingState): BookingSummary[] => {
    return state.bookings.filter(booking => {
      // For Travelski Express, don't show quotes or carts - only reservations are allowed
      if (isTravelskiExpress(booking.liheId, booking.liheName, booking.packageCode)) {
        return false // Filter out all quotes and carts for Travelski Express
      }
      // For other bookings, keep the original logic
      return booking.status === BookingStatus.Quote ||
             booking.status === BookingStatus.CartInProgress
    })
  },

  /**
   * Get selected booking
   */
  selectedBooking: (state: BookingState): BookingSummary | null => {
    return state.selectedBooking
  },
}

// Export the Vuex module
export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
